// ts-rest contract for the administrator retrieval surface of a deleted
// contact (SEP-244).
//
// A contact delete is SOFT: the row leaves every staff surface, and the firm
// keeps it in the background. An authorised administrator reads that
// background set here, and puts a row back when the delete was a mistake.
//
// This is an admin-only surface, so it sits in its own contract file with its
// own `/admin/...` paths, beside `retentionContract`, `bookHealthContract` and
// `clientBooksContract`. The controller gates it on the admin persona
// (ADR-0004). The two Object Types behind it are the person `Contact` and the
// firm-shared `OrganisationContact`, so a row names its kind.
//
// Endpoints:
//   GET  /api/admin/contacts/deleted             — the deleted contacts
//   POST /api/admin/contacts/:contactId/restore  — put one back
//
// This is a presentation projection, NOT a domain model (ADR-0003). The
// projection layer never imports the ontology runtime.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { errorBodyWithCode as errorBody } from '../schemas/common';
import { contactKindSchema } from './contacts';

const c = initContract();

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/** One deleted contact, person or organisation. */
export const deletedContactRowSchema = z.object({
    contactId: z.string(),
    contactKind: contactKindSchema,
    /** The display name the directory showed before the delete. */
    name: z.string(),
    /** The moment of the delete, as an ISO timestamp. */
    deletedAt: z.string(),
    /** The user id of the person who deleted the contact. */
    deletedBy: z.string(),
    /** The resolved staff name for `deletedBy`. Null when the row no longer
     * resolves to a user, for example after the person leaves the firm. */
    deletedByName: z.string().nullable(),
    /** The six-month mark, as an ISO date. The firm keeps a deleted contact
     * for at least this long, so the page shows how much of the minimum
     * period is left. It is a MINIMUM, not a destruction date: the retention
     * schedule and any legal hold can keep the row for longer (ADR-0012). */
    retainedUntil: z.string(),
});
export type DeletedContactRow = z.infer<typeof deletedContactRowSchema>;

export const listDeletedContactsResponseSchema = z.object({
    rows: z.array(deletedContactRowSchema),
});
export type ListDeletedContactsResponse = z.infer<typeof listDeletedContactsResponseSchema>;

const okContact = z.object({ ok: z.boolean(), contactId: z.string() });

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const adminContactsContract = c.router(
    {
        listDeletedContacts: {
            method: 'GET',
            path: '/admin/contacts/deleted',
            query: z
                .object({
                    search: z.string().optional(),
                })
                .optional(),
            responses: { 200: listDeletedContactsResponseSchema },
            summary:
                'Admin: the deleted contacts the firm keeps in the background, newest delete first.',
        },
        restoreContact: {
            method: 'POST',
            path: '/admin/contacts/:contactId/restore',
            body: z.object({}).optional(),
            responses: { 200: okContact, 400: errorBody, 404: errorBody },
            summary:
                'Admin: put a deleted contact back on the staff surfaces (restore-contact Action); audited.',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
